import { useEffect, useState } from 'react';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import { API_MODE, api, apiList } from '@/utils/api';
import { useRole } from '@/context/RoleContext';
import type { Wallet } from '@/types/portal';
import { formatMoney } from '@/utils/format';
import { PLATFORM } from '@/config/platform';

/** PayoutController::downloadTemplate: the header and one example row. */
const TEMPLATE =
  'Creator Wallet ID,Creator Name,Campaign Name,Payout Amount (KES),Payout Date,Notes\n' +
  `WALLET123,Jane Doe,Brand X Campaign,5000.00,${new Date().toISOString().slice(0, 10)},Optional note\n`;

interface ValidatedRow {
  walletId: string;
  creatorName: string;
  campaignName: string;
  amount: number;
  payoutDate: string;
  notes: string;
  valid: boolean;
  errors: string[];
  status: 'Valid' | 'Invalid' | 'Wallet Not Found';
}

interface Validation {
  rows: ValidatedRow[];
  validCount: number;
  invalidCount: number;
  totalAmount: number;
  /** The paying wallet's available balance; null when the backend didn't check it. */
  availableBalance: number | null;
  /** Whether the wallet covers the total. False also when the backend didn't check. */
  sufficient: boolean;
}

interface PayoutUploadModalProps {
  open: boolean;
  onClose: () => void;
  /** After a batch is submitted, so the page can reload its payouts. */
  onSubmitted: (message: string) => void;
}

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([TEMPLATE], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'payout_template.csv';
  link.click();
  URL.revokeObjectURL(url);
}

/** The live portal's payout upload (template → validate → review → confirm), as one dialog. */
export default function PayoutUploadModal({ open, onClose, onSubmitted }: PayoutUploadModalProps) {
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState<Validation | null>(null);
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [walletId, setWalletId] = useState('');
  /** Set once the batch is submitted and the backend wants the OTP. */
  const [otpFor, setOtpFor] = useState<{ count: number; total: number } | null>(null);
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setCsv('');
    setFileName('');
    setResult(null);
    setOtpFor(null);
    setOtp('');
    setError('');
    // Payouts are in KES, paid from one of the agency's active wallets.
    apiList<Wallet>('/wallets?ownerType=Funding')
      .then(({ data }) => {
        const usable = data.filter((wallet) => wallet.status === 'Wallet active' && wallet.currency === 'KES');
        setWallets(usable);
        setWalletId(usable[0]?.id ?? '');
      })
      .catch(() => setError('Could not load your wallets.'));
  }, [open]);

  const wallet = wallets.find((item) => item.id === walletId);

  const pickFile = (file: File | undefined) => {
    setError('');
    setResult(null);
    setCsv('');
    setFileName('');
    if (!file) return;
    if (!/\.(csv|txt)$/i.test(file.name)) {
      setError('Upload a .csv file.');
      return;
    }
    file
      .text()
      .then((content) => {
        setCsv(content);
        setFileName(file.name);
      })
      .catch(() => setError('Could not read that file.'));
  };

  const validate = async () => {
    setBusy(true);
    setError('');
    try {
      setResult(await api<Validation>('/payouts/validate', { method: 'POST', body: JSON.stringify({ csv, walletId }) }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not check the file.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!result) return;
    setBusy(true);
    setError('');
    try {
      const reply = await api<{ requiresOtp: boolean }>('/payouts/confirm', { method: 'POST', body: JSON.stringify({}) });
      if (reply.requiresOtp) {
        setOtpFor({ count: result.validCount, total: result.totalAmount });
        setOtp('');
      } else {
        onSubmitted(`${result.validCount} payout${result.validCount === 1 ? '' : 's'} paid.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit the payouts.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!otpFor) return;
    setBusy(true);
    setError('');
    try {
      await api('/payouts/verify-otp', { method: 'POST', body: JSON.stringify({ otp }) });
      onSubmitted(`${otpFor.count} payout${otpFor.count === 1 ? '' : 's'} paid.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not verify the code.');
    } finally {
      setBusy(false);
    }
  };

  const { can } = useRole();
  // Production pays as soon as the OTP is entered, with no approval step (BACKEND_GAPS #64).
  const sendBlocked = API_MODE === 'agency' && !can('payments.approve');
  const canSubmit = Boolean(result && result.validCount > 0 && result.sufficient && !sendBlocked);

  // Leaving at the code step hands back the amount the backend took from the wallet on submit.
  const close = () => {
    if (otpFor && !busy) void api('/payouts/cancel', { method: 'POST' }).catch(() => undefined);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      icon="ri-upload-cloud-2-line"
      title="Upload payouts"
      subtitle="Pay many creators from one CSV file"
      widthClassName="max-w-3xl"
      footer={
        otpFor ? (
          <>
            <Button variant="outline" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button icon="ri-checkbox-circle-line" disabled={busy || otp.length !== 6} onClick={() => void verify()}>
              {busy ? 'Verifying…' : 'Verify and pay'}
            </Button>
          </>
        ) : result ? (
          <>
            <Button variant="outline" onClick={() => setResult(null)} disabled={busy}>
              Choose another file
            </Button>
            <Button icon="ri-send-plane-line" disabled={busy || !canSubmit} onClick={() => void confirm()}>
              {busy ? 'Submitting…' : `Submit ${result.validCount} payout${result.validCount === 1 ? '' : 's'}`}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button icon="ri-check-double-line" disabled={!csv || !walletId || busy} onClick={() => void validate()}>
              {busy ? 'Checking…' : 'Check file'}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        {error ? (
          <p role="alert" className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5" />
            {error}
          </p>
        ) : null}

        {otpFor ? (
          <div className="space-y-3">
            <p className="text-sm text-foreground-700">
              Enter the 6-digit code sent to your phone to pay {otpFor.count} creator{otpFor.count === 1 ? '' : 's'}{' '}
              {formatMoney(otpFor.total, 'KES')} from {wallet?.ownerName ?? 'your wallet'}. The code expires after 5 minutes.
            </p>
            <label htmlFor="payout-otp" className="text-xs font-label font-medium text-foreground-600">
              Enter OTP Code
            </label>
            <input
              id="payout-otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={otp}
              onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
              className="h-10 w-full max-w-xs rounded-md border border-background-300 bg-background-50 px-3 text-center font-label text-lg tracking-[0.4em] text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </div>
        ) : result ? (
          <>
            {sendBlocked ? (
              <p className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2 text-xs text-secondary-900">
                <i className="ri-lock-line mt-0.5 text-sm" />
                Only an approver (Admin or Finance) can submit payouts.
              </p>
            ) : null}
            {result.availableBalance == null ? (
              <p className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2 text-xs text-secondary-900">
                <i className="ri-error-warning-line mt-0.5 text-sm" />
                The backend didn&apos;t check your wallet balance, so this file can&apos;t be submitted yet.
              </p>
            ) : !result.sufficient ? (
              <p className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-xs text-primary-800">
                <i className="ri-error-warning-line mt-0.5 text-sm" />
                {wallet?.ownerName ?? 'This wallet'} has {formatMoney(result.availableBalance, 'KES')} available, less than the{' '}
                {formatMoney(result.totalAmount, 'KES')} this file pays. Top it up or pay fewer rows.
              </p>
            ) : (
              <p className="text-xs text-foreground-600">
                Paid from {wallet?.ownerName ?? 'your wallet'} · {formatMoney(result.availableBalance, 'KES')} available
              </p>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2">
                <p className="text-[11px] text-foreground-500">Ready to pay</p>
                <p className="font-heading text-lg font-semibold text-accent-700">{result.validCount}</p>
              </div>
              <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2">
                <p className="text-[11px] text-foreground-500">With problems</p>
                <p className="font-heading text-lg font-semibold text-primary-700">{result.invalidCount}</p>
              </div>
              <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2">
                <p className="text-[11px] text-foreground-500">Total to pay</p>
                <p className="font-heading text-lg font-semibold text-foreground-950">{formatMoney(result.totalAmount, 'KES')}</p>
              </div>
            </div>
            <div className="overflow-x-auto rounded-md border border-background-200">
              <table className="row-accent w-full min-w-[640px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-[11px] font-label uppercase tracking-wider text-foreground-400">
                    <th className="px-3 py-2 font-semibold">Wallet</th>
                    <th className="px-3 py-2 font-semibold">Creator</th>
                    <th className="px-3 py-2 font-semibold">Campaign</th>
                    <th className="px-3 py-2 font-semibold">Amount</th>
                    <th className="px-3 py-2 font-semibold">Date</th>
                    <th className="px-3 py-2 font-semibold">Check</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row, index) => (
                    <tr key={`${row.walletId}-${index}`} className="border-b border-background-100 last:border-0 align-top">
                      <td className="px-3 py-2 font-label text-xs">{row.walletId || '—'}</td>
                      <td className="px-3 py-2">{row.creatorName || '—'}</td>
                      <td className="px-3 py-2">{row.campaignName || '—'}</td>
                      <td className="px-3 py-2">{row.amount ? formatMoney(row.amount, 'KES') : '—'}</td>
                      <td className="px-3 py-2 text-xs">{row.payoutDate}</td>
                      <td className="px-3 py-2">
                        <Badge tone={row.valid ? 'accent' : 'primary'}>{row.status}</Badge>
                        {row.errors.length ? <p className="mt-1 text-xs text-primary-700">{row.errors.join(' · ')}</p> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {result.invalidCount > 0 ? (
              <p className="text-xs text-foreground-500">Rows with problems are left out. Fix them in the file and upload it again to include them.</p>
            ) : null}
          </>
        ) : (
          <>
            <p className="text-sm text-foreground-600">
              One payout per row: the creator&apos;s {PLATFORM.walletName} ID, their name, the campaign, the amount in KES, the payout
              date and an optional note. The file is checked before anything is paid.
            </p>
            <div>
              <label htmlFor="payout-wallet" className="text-xs font-label font-medium text-foreground-600">
                Pay from <span className="text-primary-600">*</span>
              </label>
              <select
                id="payout-wallet"
                value={walletId}
                onChange={(event) => setWalletId(event.target.value)}
                className="mt-1.5 h-10 w-full max-w-md rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 cursor-pointer focus:border-primary-400 focus:outline-none"
              >
                {wallets.length === 0 ? <option value="">No active KES wallet</option> : null}
                {wallets.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.ownerName} · {formatMoney(item.balance, item.currency)} available
                  </option>
                ))}
              </select>
            </div>
            <Button variant="outline" size="sm" icon="ri-download-2-line" onClick={downloadTemplate}>
              Download the template
            </Button>
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100 px-4 py-6 text-center text-sm text-foreground-600 hover:bg-background-200">
              <i className="ri-upload-cloud-2-line text-xl text-foreground-400" />
              {fileName ? <span className="font-medium text-foreground-900">{fileName}</span> : 'Choose a .csv file'}
              <input type="file" accept=".csv,text/csv,.txt" className="sr-only" onChange={(e) => pickFile(e.target.files?.[0])} />
            </label>
          </>
        )}
      </div>
    </Modal>
  );
}
